import {clsx} from 'clsx'
import {useCallback, useRef, useState} from 'react'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'

import {MonaCanvas, type MonaCanvasBounds, type MonaColorVariant} from '../MonaCanvas'
import styles from './MonaDivider.module.css'
import blackFallback from './assets/fallback/mona-divider-black.webp'
import pearlFallback from './assets/fallback/mona-divider-pearl.webp'
import purpleFallback from './assets/fallback/mona-divider-purple.webp'
import {MonaDividerGrid} from './grid/MonaDividerGrid'
import {createMonaDividerOpeningAnimation} from './animation/opening-animation'
import {
  areMonaDividerGridFocusRangesEqual,
  getMonaDividerGridFocusRange,
  type MonaDividerGridFocusRange,
} from './grid/grid-focus'

export type MonaDividerProps = {
  className?: string
  colorVariant?: MonaColorVariant
  alt?: string
}

const fallbackImages = {
  black: blackFallback,
  pearl: pearlFallback,
  purple: purpleFallback,
} satisfies Record<MonaColorVariant, string>

export function MonaDivider({className, colorVariant = 'black', alt = ''}: MonaDividerProps) {
  const prefersReducedMotion = usePrefersReducedMotion()
  const [showFallback, setShowFallback] = useState(false)
  const [focusedRange, setFocusedRange] = useState<MonaDividerGridFocusRange | null>(null)
  const focusedRangeRef = useRef<MonaDividerGridFocusRange | null>(null)

  const handleFallback = useCallback(() => setShowFallback(true), [])

  const handleMonaCanvasBoundsChange = useCallback((bounds: MonaCanvasBounds | null) => {
    const nextFocusedRange = getMonaDividerGridFocusRange(bounds)
    if (areMonaDividerGridFocusRangesEqual(focusedRangeRef.current, nextFocusedRange)) return

    focusedRangeRef.current = nextFocusedRange
    setFocusedRange(nextFocusedRange)
  }, [])

  return (
    <div
      className={clsx(styles.MonaDivider, className)}
      role={alt ? 'img' : undefined}
      aria-label={alt || undefined}
      aria-hidden={alt ? undefined : true}
    >
      <div className={styles.Wrapper}>
        {showFallback || prefersReducedMotion ? (
          <img
            alt=""
            aria-hidden="true"
            className={styles.Fallback}
            height={400}
            src={fallbackImages[colorVariant]}
            width={2592}
          />
        ) : (
          <>
            <MonaDividerGrid focusedRange={focusedRange} />
            <MonaCanvas
              colorVariant={colorVariant}
              createOpeningAnimation={createMonaDividerOpeningAnimation}
              mouseInteractionType="follow"
              onFallback={handleFallback}
              onMonaCanvasBoundsChange={handleMonaCanvasBoundsChange}
            />
          </>
        )}
      </div>
    </div>
  )
}
